import { createSelector } from '@ngrx/store';

import { AppState } from 'store/root-reducer';

import { IndividualRecord, CorporateRecord } from 'modules/shared/models/record.models';
import { RecordsResponse, RecordOtherDataResponse } from 'store/models/record.models';
import { RecordActions, RecordActionTypes } from 'store/actions/record.action';
import { CrpRolesResponse } from 'store/models/crp.models';

export interface State {
  loading: Array<RecordActionTypes>;
  records: RecordsResponse;
  individualRecord: IndividualRecord;
  corporateRecord: CorporateRecord;
  crpRoles: CrpRolesResponse;
  err: string | undefined;
  otherData: RecordOtherDataResponse;
}

const initialState: State = {
  loading: [],
  records: null,
  individualRecord: undefined,
  corporateRecord: undefined,
  crpRoles: undefined,
  err: undefined,
  otherData: null,
};

export function reducer(state: State = initialState, action: RecordActions): State {
  switch (action.type) {
    case RecordActionTypes.GET_RECORDS_SUCCESS: {
      return {
        ...state,
        records: action.payload,
        loading: clearLoading(state, RecordActionTypes.GET_RECORDS)
      };
    }
    case RecordActionTypes.GET_RECORDS_FAILURE: {
      return {
        ...state,
        err: action.payload,
        loading: clearLoading(state, RecordActionTypes.GET_RECORDS)
      };
    }
    case RecordActionTypes.GET_INDIVIDUAL_RECORD: {
      return {
        ...state,
        loading: [...state.loading, action.type]
      };
    }
    case RecordActionTypes.GET_INDIVIDUAL_RECORD_SUCCESS: {
      return {
        ...state,
        individualRecord: action.payload,
        loading: clearLoading(state, RecordActionTypes.GET_INDIVIDUAL_RECORD)
      };
    }
    case RecordActionTypes.GET_INDIVIDUAL_RECORD_FAILURE: {
      return {
        ...state,
        err: action.payload,
        loading: clearLoading(state, RecordActionTypes.GET_INDIVIDUAL_RECORD)
      };
    }
    case RecordActionTypes.GET_CORPORATE_RECORD: {
      return {
        ...state,
        loading: [...state.loading, action.type]
      };
    }
    case RecordActionTypes.GET_CORPORATE_RECORD_SUCCESS: {
      return {
        ...state,
        corporateRecord: action.payload,
        loading: clearLoading(state, RecordActionTypes.GET_CORPORATE_RECORD)
      };
    }
    case RecordActionTypes.GET_CORPORATE_RECORD_FAILURE: {
      return {
        ...state,
        err: action.payload,
        loading: clearLoading(state, RecordActionTypes.GET_CORPORATE_RECORD)
      };
    }
    case RecordActionTypes.GET_PRIMARY_CRP_RECORD: {
      return {
        ...state,
        loading: [...state.loading, action.type]
      };
    }
    case RecordActionTypes.CLEAR_GET_PRIMARY_CRP_RECORD: {
      return {
        ...state,
        individualRecord: null,
        corporateRecord: null,
        loading: clearLoading(state, RecordActionTypes.GET_PRIMARY_CRP_RECORD),
      };
    }
    case RecordActionTypes.ADD_CRP_ROLE: {
      return {
        ...state,
        loading: [...state.loading, action.type]
      };
    }
    case RecordActionTypes.ADD_CRP_ROLE_FAILURE: {
      return {
        ...state,
        err: action.payload,
        loading: clearLoading(state, RecordActionTypes.ADD_CRP_ROLE),
      };
    }
    case RecordActionTypes.DELETE_CRP: {
      return {
        ...state,
        loading: [...state.loading, action.type]
      };
    }
    case RecordActionTypes.DELETE_CRP_SUCCESS: {
      return {
        ...state,
        loading: clearLoading(state, RecordActionTypes.DELETE_CRP)
      };
    }
    case RecordActionTypes.DELETE_CRP_FAILURE: {
      return {
        ...state,
        err: action.payload,
        loading: clearLoading(state, RecordActionTypes.DELETE_CRP)
      };
    }
    case RecordActionTypes.DELETE_ROLE: {
      return {
        ...state,
        loading: [...state.loading, action.type]
      };
    }
    case RecordActionTypes.DELETE_ROLE_SUCCESS: {
      return {
        ...state,
        loading: clearLoading(state, RecordActionTypes.DELETE_ROLE)
      };
    }
    case RecordActionTypes.DELETE_ROLE_FAILURE: {
      return {
        ...state,
        err: action.payload,
        loading: clearLoading(state, RecordActionTypes.DELETE_ROLE)
      };
    }
    case RecordActionTypes.GET_CRP_ROLES: {
      return {
        ...state,
        loading: [...state.loading, action.type]
      };
    }
    case RecordActionTypes.GET_CRP_ROLES_SUCCESS: {
      return {
        ...state,
        crpRoles: action.payload,
        loading: clearLoading(state, RecordActionTypes.GET_CRP_ROLES)
      };
    }
    case RecordActionTypes.GET_CRP_ROLES_FAILURE: {
      return {
        ...state,
        err: action.payload,
        loading: clearLoading(state, RecordActionTypes.GET_CRP_ROLES)
      };
    }
    case RecordActionTypes.RESET_RECORD: {
      return Object.assign({}, initialState);
    }
    case RecordActionTypes.GET_OTHER_DATA: {
      return {
        ...state,
        loading: [...state.loading, action.type]
      };
    }
    case RecordActionTypes.GET_OTHER_DATA_SUCCESS: {
      return {
        ...state,
        loading: clearLoading(state, RecordActionTypes.GET_OTHER_DATA),
        otherData: action.payload
      };
    }
    case RecordActionTypes.GET_OTHER_DATA_FAILURE: {
      return {
        ...state,
        loading: clearLoading(state, RecordActionTypes.GET_OTHER_DATA),
        err: action.payload
      };
    }
    default: {
      return state;
    }
  }
}

const record = (state: AppState) => state.record;

export const Selectors = {
  getLoading: createSelector(record, (state: State) => state.loading),
  getRecords: createSelector(record, (state: State) => state.records),
  getIndividualRecord: createSelector(record, (state: State) => state.individualRecord),
  getCorporateRecord: createSelector(record, (state: State) => state.corporateRecord),
  getCrpRoles: createSelector(record, (state: State) => state.crpRoles),
  getErrors: createSelector(record, (state: State) => state.err),
  getRecordOtherData: createSelector(record, (state: State) => state.otherData),
};

function clearLoading(authState: State, action: RecordActionTypes): Array<RecordActionTypes> {
  return authState.loading.filter((loadingMarker: RecordActionTypes) => loadingMarker !== action);
}
